/* eslint-disable redos/no-vulnerable */
import React from 'react';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { capitalCase } from 'change-case';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import ReactMarkdown, { Components } from 'react-markdown';
import { useParams } from 'react-router';
import remarkGfm from 'remark-gfm';
import { Button, Typography } from 'antd';
import { EMPTY, PRETTY_DATE_FORMAT } from '@owl-frontend/components';
import type { UnionExpand } from '@owl-lib/type-util';
import type {
  Citation,
  CitationType,
  Summary,
} from '../../api/claims/interface';
import { Locale } from '../../context/AppProvider';
import {
  ClaimDetailsContext,
  Params,
} from '../ClaimOverview/ClaimDetailsContext';
import useFilesHook from '../ClaimOverview/FilesListContainer/useFilesHook';
import { extractCitationGroups } from './citationGroups';
import { EscCitation } from './Citation';
import CitationGroup from './CitationGroup';
import styles from './Markdown.module.scss';

interface MarkdownComponentProps {
  text: string;
  summary?: Summary;
  showReferences?: boolean;
  showTooltip?: boolean;
  hidePage?: boolean;
  customCitationHandler?(citation: Citation): void;
  citationIndex?: string;
  stateDb?: boolean;
  onPageLink?(path: string): void;
}

export const EscMarkdown: React.FC<MarkdownComponentProps> = ({
  text,
  showReferences,
  showTooltip = true,
  hidePage = false,
  customCitationHandler,
  citationIndex,
  stateDb,
  onPageLink,
}) => {
  const { dossierId } = useParams() as UnionExpand<Params>;
  const markdownRef = React.useRef<HTMLDivElement>(null);
  const [showCitations, setShowCitations] = React.useState<boolean>(false);

  const { text: streamedText, groups: citationGroups } = React.useMemo(
    () => extractCitationGroups(text),
    [text]
  );

  const citationNumberCitationMap = React.useMemo(
    () =>
      Object.values(citationGroups)
        .flat()
        .reduce(
          (acc, citation) => ({ ...acc, [citation.citationNumber]: citation }),
          {}
        ),
    [citationGroups]
  );

  const intl = useIntl();
  const { messages } = React.useContext(Locale);
  const {
    activities: { currentActivities },
  } = React.useContext(ClaimDetailsContext);
  const [{ files }] = useFilesHook(dossierId);
  const currentFiles = files;
  const generateCitationText = (citationIdx, fileId, type) => {
    if (type === 'NOTE') {
      const note = currentActivities.find((a) => a.activity_id === fileId);
      if (!note) {
        return EMPTY;
      }

      const noteStartDate = note.start_date;
      const noteSubject = note.activity_subject;

      return intl.formatMessage(
        { id: 'claim_overview.ask_owl.citations_note' },
        {
          citationIndex: citationIdx,
          date: dayjs(noteStartDate).format(PRETTY_DATE_FORMAT),
          noteTitle: capitalCase(noteSubject ?? ''),
        }
      );
    }

    if (type === 'METADATA') {
      return messages['claim_overview.ask_owl.citations_metadata'];
    }

    const file = currentFiles?.[fileId];

    if (!file) {
      return EMPTY;
    }

    return `${citationIdx}. ${file.file_name}`;
  };

  const components: Components = React.useMemo(
    () => ({
      td: ({ children }) => {
        let result: React.ReactNode[] = [];

        if (Array.isArray(children)) {
          for (const child of children) {
            if (child === '<br>') {
              result.push('\n');
            } else {
              result.push(child);
            }
          }
        } else {
          result = [children];
        }

        return <td>{result}</td>;
      },
      a: ({ children, href, ...props }) => {
        if (href) {
          const match = href.match(/^citation-(\d+)$/);
          if (match) {
            const matchIndex = match[1];
            const group = citationGroups[matchIndex];

            if (group?.length) {
              return (
                <CitationGroup key={matchIndex}>
                  {group.map((citation, i) => (
                    <EscCitation
                      key={`${matchIndex}-${i}`}
                      citationIndex={
                        citationIndex ?? `${citation.citationNumber}`
                      }
                      citation={{
                        ...citation,
                        citation_id: href,
                        fragment_type: citation.type as CitationType,
                        pages: [citation.start_page, citation.end_page],
                      }}
                      customCitationHandler={customCitationHandler}
                      {...props}
                      showTooltip={showTooltip}
                      hidePage={hidePage}
                      stateDb={stateDb}
                    />
                  ))}
                </CitationGroup>
              );
            }
          }
        }
        if (onPageLink && href && /\.md(#.+)?$/.test(href)) {
          const path = href.replace(/^(\.\.?\/)+/, '');
          return (
            <a
              href={href}
              onClick={(e) => {
                e.preventDefault();
                onPageLink(path);
              }}
              {...props}
            >
              {children}
            </a>
          );
        }
        return (
          <a href={href} {...props}>
            {children}
          </a>
        );
      },
    }),
    [
      citationGroups,
      customCitationHandler,
      showTooltip,
      hidePage,
      citationIndex,
      stateDb,
      onPageLink,
    ]
  );

  return (
    <>
      <div className={styles.markdown} ref={markdownRef}>
        <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
          {streamedText}
        </ReactMarkdown>
      </div>
      {showReferences && Object.keys(citationGroups).length > 0 && (
        <div className={styles.references}>
          <Button type="link" onClick={() => setShowCitations((prev) => !prev)}>
            <Typography.Text strong>
              {messages['claim_overview.ask_owl.references']}
            </Typography.Text>
            {showCitations ? <UpOutlined /> : <DownOutlined />}
          </Button>
          {showCitations &&
            Object.keys(citationNumberCitationMap).map((citationNumber) => (
              <EscCitation
                key={citationNumber}
                citation={{
                  citation_id:
                    citationNumberCitationMap[citationNumber].fragment_id,
                  file_id: citationNumberCitationMap[citationNumber].file_id,
                  pages: [1, 1],
                  fragment_type: citationNumberCitationMap[citationNumber].type,
                }}
                stateDb={stateDb}
              >
                <Button className={styles.citationButton}>
                  {generateCitationText(
                    citationNumberCitationMap[citationNumber].citationNumber,
                    citationNumberCitationMap[citationNumber].file_id,
                    citationNumberCitationMap[citationNumber].type
                  )}
                </Button>
              </EscCitation>
            ))}
        </div>
      )}
    </>
  );
};
